import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, Upload, Plus, X, RefreshCw, CreditCard as Edit2, Sparkles, IndianRupee, Shield, ImagePlus, CreditCard as Edit, Youtube, AlertCircle, Tag, Loader, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { CollectibleType } from './CollectiblesList';
import { generateSlug } from '../lib/supabase';
import ImageEditor from './ImageEditor';
import CameraCapture from './CameraCapture';

interface ImageData {
  id: string;
  url: string;
  file?: File;
  type: 'file' | 'url';
}

interface Category {
  id: string;
  name: string;
}

interface FormData {
  type: CollectibleType;
  name: string;
  slug: string;
  description: string;
  price: string;
  youtube_url?: string;
  imageUrl: string;
  category_id: string;
}

function UploadCollectible() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<ImageData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<FormData>({
    type: 'coin',
    name: '',
    slug: '',
    description: '',
    price: '',
    youtube_url: '',
    imageUrl: '',
    category_id: ''
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
        
        // Find and set Mixed Collectibles as default
        const mixedCategory = data?.find(cat => cat.name === 'Mixed Collectibles');
        if (mixedCategory) {
          setFormData(prev => ({ ...prev, category_id: mixedCategory.id }));
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };

    fetchCategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug === generateSlug(prev.name) ? generateSlug(value) : prev.slug
    }));
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      slug: generateSlug(value)
    }));
  };

  const analyzeWithEdgeFunction = async (imageUrl: string, title?: string) => {
    const { data, error } = await supabase.functions.invoke('analyze-collectible', {
      body: { imageUrl, title }
    });

    if (error) {
      throw new Error(error.message || 'Analysis failed');
    }

    return data;
  };

  const analyzeImages = async (imageUrls: string[], useTitle?: string) => {
    if (imageUrls.length === 0) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      // Analyze first two images, optionally with title context
      const analysisPromises = imageUrls.slice(0, 2).map(url => analyzeWithEdgeFunction(url, useTitle));
      const analyses = await Promise.all(analysisPromises);

      // Combine analyses results
      const combinedAnalysis = {
        type: analyses[0].type || 'coin',
        name: analyses[0].name || '',
        description: analyses.map(a => a.description).filter(Boolean).join('\n\n'),
        suggestedPrice: Math.max(...analyses.map(a => a.suggestedPrice || 0))
      };

      setFormData(prev => ({
        ...prev,
        type: combinedAnalysis.type,
        name: combinedAnalysis.name,
        description: combinedAnalysis.description,
        price: combinedAnalysis.suggestedPrice?.toString() || prev.price,
        slug: generateSlug(combinedAnalysis.name)
      }));
    } catch (err) {
      console.error('Error analyzing images:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`AI analysis failed: ${msg}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, useCamera: boolean = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = 6 - images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    const newImages = await Promise.all(
      filesToProcess.map(async (file) => {
        const reader = new FileReader();
        const imageUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        return {
          id: Math.random().toString(36).substring(7),
          url: imageUrl,
          file,
          type: 'file' as const
        };
      })
    );

    setImages(prev => [...prev, ...newImages]);

    if (useCamera) {
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    } else {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    // If we have at least one image, analyze the first two
    if (images.length === 0 && newImages.length > 0) {
      await analyzeImages(newImages.slice(0, 2).map(img => img.url));
    }
  };

  const handleUrlImage = () => {
    if (!formData.imageUrl.trim() || images.length >= 6) return;

    const newImage = {
      id: Math.random().toString(36).substring(7),
      url: formData.imageUrl,
      type: 'url' as const
    };

    setImages(prev => [...prev, newImage]);
    setFormData(prev => ({ ...prev, imageUrl: '' }));

    // If this is the first or second image, analyze it
    if (images.length < 2) {
      analyzeImages([...images, newImage].slice(0, 2).map(img => img.url));
    }
  };

  const removeImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    setImages(prev => {
      const newImages = [...prev];
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      return newImages;
    });
  };

  const handleImageEdit = (editedImage: string) => {
    if (selectedImageIndex === null) return;

    const byteString = atob(editedImage.split(',')[1]);
    const mimeString = editedImage.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `edited-image-${Date.now()}.jpg`, { type: mimeString });

    setImages(prev => {
      const newImages = [...prev];
      newImages[selectedImageIndex] = {
        id: Math.random().toString(36).substring(7),
        url: editedImage,
        file,
        type: 'file'
      };
      return newImages;
    });

    setShowImageEditor(false);
    setSelectedImageIndex(null);

    // If this is one of the first two images, reanalyze
    if (selectedImageIndex < 2) {
      const updatedImages = [...images];
      updatedImages[selectedImageIndex] = {
        id: Math.random().toString(36).substring(7),
        url: editedImage,
        file,
        type: 'file'
      };
      analyzeImages(updatedImages.slice(0, 2).map(img => img.url));
    }
  };

  const handleCameraCapture = (imageData: string) => {
    const byteString = atob(imageData.split(',')[1]);
    const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `camera-image-${Date.now()}.jpg`, { type: mimeString });

    const newImage = {
      id: Math.random().toString(36).substring(7),
      url: imageData,
      file,
      type: 'file' as const
    };

    setImages(prev => [...prev, newImage]);
    setShowCamera(false);

    // If this is one of the first two images, analyze
    if (images.length < 2) {
      analyzeImages([...images, newImage].slice(0, 2).map(img => img.url));
    }
  };

  const retryAnalysis = async () => {
    if (images.length === 0) {
      setError('Please add at least one image to analyze');
      return;
    }

    await analyzeImages(images.slice(0, 2).map(img => img.url));
  };

  const regenerateDescription = async () => {
    if (images.length === 0) {
      setError('Please add at least one image');
      return;
    }

    if (!formData.name.trim()) {
      setError('Please enter a title first');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Analyze with title context to generate description based on the title
      const analysis = await analyzeWithEdgeFunction(images[0].url, formData.name);

      setFormData(prev => ({
        ...prev,
        description: analysis.description,
        // Keep the existing title but update description
      }));
    } catch (err) {
      console.error('Error regenerating description:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`AI regeneration failed: ${msg}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (images.length === 0) {
      setError('Please add at least one image');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create collectible
      const { data: collectible, error: collectibleError } = await supabase
        .from('collectibles')
        .insert({
          type: formData.type,
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          price: parseInt(formData.price, 10),
          youtube_url: formData.youtube_url || null,
          category_id: formData.category_id || null
        })
        .select()
        .single();

      if (collectibleError || !collectible) throw collectibleError;

      // Upload images
      const imagePromises = images.map(async (image, index) => {
        if (image.type === 'file' && image.file) {
          const fileName = `${collectible.id}/${Date.now()}-${index}`;
          const { error: uploadError } = await supabase.storage
            .from('collectible-images')
            .upload(fileName, image.file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('collectible-images')
            .getPublicUrl(fileName);

          return supabase
            .from('collectible_images')
            .insert({
              collectible_id: collectible.id,
              image_url: publicUrl,
              order: index
            });
        } else if (image.type === 'url') {
          return supabase
            .from('collectible_images')
            .insert({
              collectible_id: collectible.id,
              image_url: image.url,
              order: index
            });
        }
      });

      await Promise.all(imagePromises);
      navigate('/', { state: { successMessage: 'Item listed successfully!' } });
    } catch (err) {
      console.error('Error creating collectible:', err);
      setError(err instanceof Error ? err.message : 'Failed to create collectible');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
          <div className="relative">
            <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-100">
              <Upload className="h-6 w-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary-400 rounded-full border-2 border-white animate-pulse" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
              Create New Listing
            </h1>
            <p className="text-gray-500">Add a new item to your collection</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Images</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Add up to 6 high-quality images
                </p>
              </div>

              {images.length > 0 && (
                <button
                  type="button"
                  onClick={retryAnalysis}
                  disabled={isAnalyzing}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Rewrite with AI
                    </>
                  )}
                </button>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e, false)}
            />

            <input
              type="file"
              ref={cameraInputRef}
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFileSelect(e, true)}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
              {images.length < 6 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                    className="aspect-square flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                  >
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-600">Upload</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (cameraInputRef.current) {
                        cameraInputRef.current.click();
                      }
                    }}
                    className="aspect-square flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                  >
                    <Camera className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-600">Use Camera</span>
                  </button>
                </>
              )}

              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="aspect-square relative group rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent hover:border-primary-300 transition-colors"
                >
                  <div className="absolute top-1 left-1 z-10 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    {index + 1}
                  </div>
                  <img
                    src={image.url}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveImage(index, 'up')}
                          disabled={index === 0}
                          className="p-1.5 bg-white rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move left"
                        >
                          <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(index, 'down')}
                          disabled={index === images.length - 1}
                          className="p-1.5 bg-white rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move right"
                        >
                          <ChevronDown className="h-4 w-4 rotate-[90deg]" />
                        </button>
                      </div>
                      <div className="flex gap-1 justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImageIndex(index);
                            setShowImageEditor(true);
                          }}
                          className="p-1.5 bg-white rounded-lg text-primary-600 hover:text-primary-700 transition-colors"
                          title="Edit image"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="p-1.5 bg-white rounded-lg text-red-500 hover:text-red-700 transition-colors"
                          title="Remove image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {images.length < 6 && (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="url"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    placeholder="Or enter image URL"
                  />
                  <ImagePlus className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
                <button
                  type="button"
                  onClick={handleUrlImage}
                  className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="coin">Coin</option>
                <option value="note">Note</option>
              </select>
            </div>

            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              required
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Enter a descriptive title"
            />
          </div>

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              required
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="unique-url-friendly-identifier"
            />
          </div>

          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Price (₹)
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              min="0"
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Enter price in INR"
            />
          </div>

          <div>
            <label htmlFor="youtube_url" className="block text-sm font-medium text-gray-700 mb-1">
              YouTube Video URL (optional)
            </label>
            <div className="relative">
              <input
                type="url"
                id="youtube_url"
                name="youtube_url"
                value={formData.youtube_url}
                onChange={handleChange}
                pattern="^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}$"
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="e.g., https://youtube.com/watch?v=..."
              />
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Add a YouTube video showcasing the collectible. Must be a valid YouTube video URL.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <button
                type="button"
                onClick={regenerateDescription}
                disabled={images.length === 0 || !formData.name.trim() || isAnalyzing}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {isAnalyzing ? (
                  <>
                    <Loader className="h-3 w-3 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3" />
                    Regenerate from Title
                  </>
                )}
              </button>
            </div>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Describe the item in detail including condition, authenticity, historical significance, and any other relevant details"
            />
            <p className="mt-1 text-xs text-gray-500">
              Edit the title above, then click "Regenerate from Title" to create a description matching your title
            </p>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-1 bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors shadow-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={images.length === 0 || isSubmitting}
              className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed shadow-sm font-medium"
            >
              {isSubmitting ? 'Creating listing...' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>

      {showImageEditor && selectedImageIndex !== null && (
        <ImageEditor
          imageUrl={images[selectedImageIndex].url}
          onSave={handleImageEdit}
          onClose={() => {
            setShowImageEditor(false);
            setSelectedImageIndex(null);
          }}
        />
      )}

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}

export default UploadCollectible;